import * as R from "ramda";
import { behaveAs } from "common/entities";
import { PartLocationSiteTypeSelector } from "common/record/related-entity-table/part-locations";
import { noOp } from "common";
import { getSubFKTitle, isForeignKey } from "common/functions/foreign-key";
import { searchApi } from "common/api/search";
// eslint-disable-next-line import/no-cycle
import { TableWithReorder } from "common/query/table";
import { TableValue } from "common/query/table/types";
import { isOrderField, QueryForEntity } from "common/query/types";
import { Properties, Record } from "common/types/records";
import { ValueComponent, ValueProps } from "common/with-value-for";
import { ALL_SITE, CURRENT_SITE, SiteType } from "x/records/list/filter/types";
import { getTopLevelSite } from "common/functions/sites";
import {
  getNewPartLocations,
  getPartLocationsQuery,
} from "../form/content/related/stock/functions";
import {
  getConfig,
  getQuery,
  getSortingColumn,
  getSortingFnForColumn,
  setWidthsToPreferences,
  updateRecordsWithProperties,
} from "./functions";
import { PropTypes } from "./types";

interface StateType {
  widths: number[];
  allPartLocations: Properties[];
  siteType: SiteType;
  query?: QueryForEntity;
  expandedAction?: { actionName: string; recordId: string };
}

type Props = PropTypes & ValueProps<Record[]>;

const getFieldTitle = (value: any) => {
  if (!value) return "";
  if (isForeignKey(value)) return getSubFKTitle(value.title);

  return typeof value === "string" ? value : value.toString();
};

export class RelatedEntityTable extends ValueComponent<
  Record[],
  PropTypes,
  StateType
> {
  static readonly displayName = "RelatedEntityTable";

  state: StateType = {
    widths: [],
    siteType: CURRENT_SITE,
    allPartLocations: [],
  };

  componentDidMount() {
    this.setState({ widths: this.getWidthsFromPreferences() });
  }

  componentDidUpdate(prevProps: Props) {
    const { entity, value } = this.props;
    const { allPartLocations, siteType } = this.state;
    const oldRelatedEntity = entity;
    const newRelatedEntity = prevProps.entity;

    if (oldRelatedEntity !== newRelatedEntity) {
      this.setState({ query: undefined });
    }
    if (siteType === ALL_SITE && prevProps.value !== value) {
      this.setState({
        allPartLocations: getNewPartLocations(allPartLocations, value),
      });
    }
  }

  getWidthsFromPreferences = () => {
    // could be memoized
    const { context, parentEntityName, entity } = this.props;

    const relatedPrefs = R.find(
      (setting) =>
        setting.entityName === parentEntityName &&
        setting.site === context.site.name,
      context.preferenceService.get().related,
    );
    return relatedPrefs?.widths[entity.name];
  };

  onTableChanged = (tableValue: TableValue) => {
    const { value = [], parentEntityName, entity, context } = this.props;
    const { siteType } = this.state;
    const { query, expandedAction, data, widths } = tableValue;
    const updatedData =
      siteType === ALL_SITE ? value.map((record) => record.properties) : data;

    if (widths)
      setWidthsToPreferences(
        parentEntityName,
        context.site.name,
        entity.name,
        widths,
        context.preferenceService,
      );

    this.setState({ ...(widths && { widths }), query, expandedAction });
    this.setValue(updateRecordsWithProperties(value, updatedData));
  };

  hideExpandedAction = () => {
    this.setState({ expandedAction: undefined });
  };

  getData = (value: Record[]) => {
    const { context } = this.props;
    const { siteType, allPartLocations } = this.state;
    const query = this.state.query || getQuery(this.props);
    const { order } = query.query;

    const orderBy = R.find((o) => isOrderField(o), order) ?? { name: "number" };
    const sortField = orderBy?.alias ?? orderBy?.name;

    const sortColumn = getSortingColumn(context, query, orderBy);
    const sortingFn = getSortingFnForColumn(sortColumn);

    const data =
      siteType === CURRENT_SITE
        ? value?.map((record) => record.properties) || []
        : allPartLocations;

    return data.sort((rowA, rowB) => {
      const rowASortField = getFieldTitle(rowA[sortField]);
      const rowBSortField = getFieldTitle(rowB[sortField]);

      return orderBy?.desc
        ? sortingFn(rowBSortField, rowASortField)
        : sortingFn(rowASortField, rowBSortField);
    });
  };

  onChangeSite = (site: SiteType) => {
    if (site === CURRENT_SITE) {
      this.setState({ siteType: site });
      return;
    }

    const { context, entity, recordId, withOwnerId, query, value } = this.props;
    const { apiCall, sites } = context;
    const topLevelSite = getTopLevelSite(sites);

    searchApi(apiCall)
      .runQueryForSite(
        getPartLocationsQuery(recordId, query, entity, withOwnerId),
        topLevelSite.name,
      )
      .then((records: Properties[]) => {
        this.setState({
          allPartLocations: getNewPartLocations(records, value),
          siteType: site,
        });
      });
  };

  render() {
    const {
      context,
      entity,
      value,
      readOnly,
      highlighted,
      widgetsMapper,
      withLinks = true,
      isRecordInvalid,
      getRelatedEntityTableHeader,
    } = this.props;
    const { query, expandedAction, widths, siteType } = this.state;

    const tableQuery = query || getQuery(this.props);
    const config = getConfig(this.props, tableQuery, context.entities);
    const data = this.getData(value).filter((r) => !r.isDeleted);

    // We currently don't support edit actions for cross site part locations and keeping all part locations as readonly.
    // This can be removed once that's implemented in the next feature.
    const isRecordDisabled =
      siteType === ALL_SITE ? () => true : this.props.isRecordDisabled;

    return (
      <>
        {
          // Once we implement lazy loading for related entities, this can be moved out from here to the corresponding file
          behaveAs("Stock", entity) && (
            <PartLocationSiteTypeSelector
              value={siteType}
              onChange={this.onChangeSite}
            />
          )
        }
        {getRelatedEntityTableHeader
          ? getRelatedEntityTableHeader()
          : undefined}
        <div className="x-related-table">
          <TableWithReorder
            isLoading={false}
            highlighted={highlighted}
            starred={[]}
            toggleStar={undefined}
            goTo={undefined}
            withLinks={withLinks}
            reload={noOp}
            context={context}
            config={config}
            readOnly={readOnly}
            withTotals={true}
            isRecordDisabled={isRecordDisabled}
            isRecordInvalid={isRecordInvalid}
            widgetsMapper={widgetsMapper}
            value={{
              data,
              query: tableQuery,
              secondaryQueries: undefined,
              widths,
              expandedAction,
              selected: [],
            }}
            onChange={this.onTableChanged}
          />
        </div>
      </>
    );
  }
}
